export interface ChatMessage {
  id: number;
  user: string;
  messageData: string;
  createdAt: number;
}

export interface ChatClientOpts {
  gameURL: string;
  gameId: string;
  token: string;
  selfName: string;
}

type Listener = (m: ChatMessage) => void;

export class ChatClient {
  private url: string;
  private selfName: string;
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private stopped = false;
  private reconnectMs = 1000;

  constructor(opts: ChatClientOpts) {
    const base = opts.gameURL.replace(/\/$/, "");
    this.url = `${base}/online/chat/${opts.gameId}/?token=${encodeURIComponent(
      opts.token
    )}`;
    this.selfName = opts.selfName;
  }

  start(): void {
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    if (this.pingTimer) clearInterval(this.pingTimer);
    if (this.ws) {
      try {
        this.ws.close();
      } catch {}
      this.ws = null;
    }
  }

  onMessage(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  send(text: string): void {
    if (!text) return;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ messageData: text, voiceData: "" }));
    }
  }

  private connect(): void {
    if (this.stopped) return;
    try {
      this.ws = new WebSocket(this.url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws.binaryType = "blob";
    this.ws.onopen = () => {
      this.reconnectMs = 1000;
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = setInterval(() => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send("");
        }
      }, 1000);
    };
    this.ws.onmessage = async (ev) => {
      let text: string;
      if (typeof ev.data === "string") text = ev.data;
      else if (ev.data instanceof Blob) text = await ev.data.text();
      else if (ev.data instanceof ArrayBuffer)
        text = new TextDecoder().decode(new Uint8Array(ev.data));
      else return;
      let parsed: any;
      try {
        parsed = JSON.parse(text);
      } catch {
        return;
      }
      const messageData: string = parsed.messageData ?? "";
      if (!messageData) return;
      if (parsed.user && parsed.user === this.selfName) return;
      this.listeners.forEach((l) =>
        l({
          id: typeof parsed.id === "number" ? parsed.id : Date.now(),
          user: parsed.user ?? "",
          messageData,
          createdAt: Date.now(),
        })
      );
    };
    this.ws.onerror = () => {};
    this.ws.onclose = () => {
      if (this.pingTimer) {
        clearInterval(this.pingTimer);
        this.pingTimer = null;
      }
      this.ws = null;
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    if (this.stopped) return;
    const delay = this.reconnectMs;
    this.reconnectMs = Math.min(this.reconnectMs * 2, 30000);
    setTimeout(() => this.connect(), delay);
  }
}
